import { buildApiUrl } from '~/app/lib/api-config';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

export type AtmmSelectOption = {
  value: string;
  label: string;
};

const atmmAssociationSchema = z.object({
  id: z.string(),
  assetTypeId: z.string(),
  assetTypeName: z.string(),
  manufacturerId: z.string(),
  manufacturerName: z.string(),
  assetModelId: z.string(),
  assetModelName: z.string(),
});

const atmmAssociationListSchema = z.array(atmmAssociationSchema);

type AtmmAssociation = z.infer<typeof atmmAssociationSchema>;

async function fetchAssociationsByAssetType(
  assetTypeId: string,
): Promise<AtmmAssociation[]> {
  const response = await fetch(
    buildApiUrl(
      `/api/v1/assets/asset-types/${encodeURIComponent(assetTypeId)}/manufacturer-models`,
    ),
    {
      method: 'GET',
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const errorPayload = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    throw new Error(
      (errorPayload as { message?: string }).message ||
        'Failed to fetch manufacturer model associations',
    );
  }

  return atmmAssociationListSchema.parse(await response.json());
}

/**
 * Loads allowed Asset Type × Manufacturer × Model associations for an asset type
 * via the operational assets API (ASSETS_READ) — not knowledge-base admin permissions.
 */
export function useAtmmOptionsForAssetType(
  assetTypeId: string | null | undefined,
  assetModelId?: string | null,
) {
  const query = useQuery({
    queryKey: ['assets', 'atmm', 'by-asset-type', assetTypeId],
    queryFn: () => fetchAssociationsByAssetType(assetTypeId!),
    enabled: !!assetTypeId,
  });

  const associations = query.data ?? [];

  const assetModelOptions: AtmmSelectOption[] = useMemo(() => {
    const byId = new Map<string, string>();
    for (const row of associations) {
      byId.set(row.assetModelId, row.assetModelName);
    }
    return Array.from(byId.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [associations]);

  const manufacturerOptions: AtmmSelectOption[] = useMemo(() => {
    const filtered = assetModelId
      ? associations.filter((row) => row.assetModelId === assetModelId)
      : associations;

    const byId = new Map<string, string>();
    for (const row of filtered) {
      byId.set(row.manufacturerId, row.manufacturerName);
    }
    return Array.from(byId.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [associations, assetModelId]);

  return {
    associations,
    assetModelOptions,
    manufacturerOptions,
    isLoading: query.isLoading || query.isFetching,
    error: query.error,
  };
}
